import React from 'react'

import AsyncStorage from './AsyncStorage'
import FetchHelper from './FetchHelper'

import jwtDecode from 'jwt-decode'
import moment from 'moment'
import General from './General'

var isLoggedIn = false
var accessToken = null
var refreshToken = null
var currentUser = null

var cachedValidateTokensFunction = null

const KEY_ACCESS_TOKEN = 'accessToken'
const KEY_REFRESH_TOKEN = 'refreshToken'



export default class AuthManager {
  static isAuthenticated() {
    return AuthManager.isLoggedIn
  }

  static getAccessToken() {
    return AuthManager.accessToken
  }

  static getCurrentUser() {
    return AuthManager.currentUser
  }

  static isCompanyMember() {
    return AuthManager.currentUser.role === 'company_member'
  }

  static _hasError(responseJson) {
    let hasError = false
    let tokens = responseJson.tokens

    if (!tokens) {
      hasError = true
    }

    if (tokens.length === 0) {
      hasError = true
    }

    if (!tokens.access) {
      hasError = true
    }

    if (!tokens.refresh) {
      hasError = true
    }

    return hasError
  }

  static register(data) {
    return FetchHelper.post(global.Api.Register, data, false, false)
      .then(responseJson => {
        if (this._hasError(responseJson)) {
          throw AuthManager.getError(responseJson)
        }
        AuthManager._updateTokens(responseJson.tokens)
        AuthManager._setUser(responseJson)
        return responseJson
      })
      .catch(error => {
        throw AuthManager.getError(error)
      })
  }

  static login(email, password, websiteUrl=null) {
    let data = { email, password }

    if(websiteUrl){
      data.website_url = websiteUrl
    }

    return FetchHelper.post(global.Api.User.Login, data, false, false)
      .then(responseJson => {
        if (this._hasError(responseJson)) {
          throw AuthManager.getError(responseJson)
        }
        AuthManager._updateTokens(responseJson.tokens)
        AuthManager._setUser(responseJson)

        return responseJson
      })
      .catch(error => {
        throw AuthManager.getError(error)
      })
  }

  static _getMinutesUntilTokenExpiration() {
    var decodedJWT = jwtDecode(AuthManager.accessToken)
    var exp = decodedJWT.exp * 1000
    var expirationTime = moment(exp)
    var today = moment()
    return expirationTime.diff(today, 'minutes')
  }

  static validateTokens() {
    return AuthManager.getCachedValidateTokensFunction()
  }

  static _validateTokens() {
    let remainingMinutes = AuthManager._getMinutesUntilTokenExpiration()
    if (remainingMinutes > 1) {
      setTimeout(() => {
        AuthManager.clearCachedValidateTokensFunction()
      }, 300)
      return new Promise((resolve, reject) => resolve())
    }

    return AuthManager.refreshTokens()
      .then(() => {
        AuthManager.clearCachedValidateTokensFunction()
        return true
      }).catch((error) => {
        AuthManager.clearCachedValidateTokensFunction()
        // Token has expired.
        General.setLocationHref("/login")
      })
  }

  static refreshTokens() {
    return AsyncStorage.getItem(KEY_REFRESH_TOKEN)
      .then(refreshToken => {
        if (!refreshToken) {
          throw { message: 'No Refresh Token Found' }
          return
        }
        // try and refresh the token if we find it, if this fails
        // our token has expired and we will need user to re login
        // manually
        const data = { refresh: refreshToken }

        return FetchHelper.post(global.Api.User.RefreshToken, data, false, false)
      })
      .then(tokenResponse => {
        return AuthManager._updateTokens(tokenResponse)
      })
  }

  static async hashLogin() {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const hash = params.get('h')
    if(!hash){
      return false
    }

    window.history.replaceState(null, "", window.location.pathname)

    return FetchHelper.post(`${window.Api.Base}/hashes/login`, { hash }, false, false)
      .then(tokens => {
        AuthManager._updateTokens(tokens)
        return true
      })
  }

  static silentLogin() {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const hash = params.get('h')
    if(!hash){
      return AuthManager._silentLogin()
    }

    window.history.replaceState(null, "", window.location.pathname)

    return FetchHelper.post(`${window.Api.Base}/hashes/login`, { hash }, false, false)
      .then(tokens => {
        AuthManager._updateTokens(tokens)
        return AuthManager._silentLogin()
      })
      .catch(error => {
        return AuthManager._silentLogin()
      })
  }

  static _silentLogin() {
    return AuthManager.refreshTokens()
      .then(() => {
        return FetchHelper.get(global.Api.User.Info)
      })
      .then(responseJson => {
        AuthManager._setUser(responseJson)
        return AuthManager.currentUser
      })
      .catch(error => {
        AuthManager.accessToken = null
        AuthManager.refreshToken = null
        throw error
      })
  }

  static async logOut() {
    let data = { refresh: AuthManager.refreshToken }
    return FetchHelper.post(global.Api.User.Logout, data).then(() => {
      return AuthManager.removeCredentials()
    })
  }

  static requestResetPassword(email) {
    return FetchHelper.post(
      global.Api.User.RequestResetPassword,
      {
        email
      },
      false,
      false
    )
  }

  static resetPassword(email, password, code) {
    let data = {
      email,
      password,
      verification_code: code
    }
    return FetchHelper.post(global.Api.User.User.ResetPassword, data, false, true)
  }

  static removeCredentials() {
    AuthManager.accessToken = null
    AuthManager.refreshToken = null
    AuthManager.isLoggedIn = false
    AuthManager.currentUser = null
    AsyncStorage.removeItem(KEY_ACCESS_TOKEN)
    return AsyncStorage.removeItem(KEY_REFRESH_TOKEN)
  }

  static async setTokens(){
    AuthManager.accessToken = await AsyncStorage.getItem(KEY_ACCESS_TOKEN)
    AuthManager.refreshToken = await AsyncStorage.getItem(KEY_REFRESH_TOKEN)

    return true
    return AuthManager._handleValidateTokens(AuthManager.refreshToken !== null)
  }

  static getError(error) {
    var errorMessage = 'An unexpected error occured'
    if (error.email) {
      errorMessage = error.email[0]
    } else if (error.message) {
      errorMessage = error.message
    } else if (error.non_field_errors) {
      errorMessage = error.non_field_errors[0]
    } else if (error.detail) {
      errorMessage = error.detail
    }
    return { error: errorMessage, message: errorMessage }
  }

  static _updateTokens(tokens) {
    AuthManager.accessToken = tokens.access
    AuthManager.refreshToken = tokens.refresh
    AsyncStorage.setItem(KEY_ACCESS_TOKEN, AuthManager.accessToken)
    AsyncStorage.setItem(KEY_REFRESH_TOKEN, AuthManager.refreshToken)
  }

  static _setUser(responseJson) {
    AuthManager.isLoggedIn = true
    AuthManager.currentUser = this._getCurrentUser(responseJson)
  }

  static _getCurrentUser(responseJson) {
    if (responseJson.company_member) {
      return responseJson.company_member
    }
    return null
  }

  static getHeaders(contentType = 'application/json') {
    var headers = {}
    if(contentType === 'application/json'){
      headers = { 'Content-Type': contentType }
    }
    if (AuthManager.accessToken) {
      headers['Authorization'] = 'Bearer ' + AuthManager.accessToken
    }

    return new Headers(headers)
  }

  static getCachedValidateTokensFunction(){
    if(!AuthManager.cachedValidateTokensFunction){
      AuthManager.cachedValidateTokensFunction = AuthManager._validateTokens()
    }
    return AuthManager.cachedValidateTokensFunction
  }

  static clearCachedValidateTokensFunction(){
    AuthManager.cachedValidateTokensFunction = undefined
  }
}
